import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';

import { useNavigate, useLocation } from 'react-router-dom';

import {

  Plus, Edit, Trash2, Search, Eye, Save, X, AlertCircle, BookOpen, RefreshCw,

  Building, User, Mail, AlertTriangle,

  Download, ChevronDown, ArrowUpDown, ArrowUp, ArrowDown

} from 'lucide-react';

import { useQuery, useMutation, useQueryClient, keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';

import toast from 'react-hot-toast';

import trainingService from '../../../api/services/trainingService';

import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';

import { formatDate } from '../../../utils/stringUtils';

import Button from '../../../components/common/Button/Button';

import ExportModal from '../../../components/common/ExportModal';

import {

  exportToCSV,

  generateExportFilename,

  downloadFromBackend

} from '../../../utils/exportUtils';

import { API_BASE_URL } from '../../../api/config';

import PaginationControls from '../../../components/common/Pagination/PaginationControls';

import useUrlTableState from '../../../hooks/useUrlTableState';



const USERS_PAGE_SIZE = 20;



const SearchableSingleSelect = ({

  label,

  value,

  onChange,

  options = [],

  placeholder = 'Select an option',

  error,

  touched,

  dataField,

  onBlur,

  disabled = false

}) => {

  const [open, setOpen] = useState(false);

  const [search, setSearch] = useState('');

  const containerRef = useRef(null);



  useEffect(() => {

    const handleClickOutside = (event) => {

      if (containerRef.current && !containerRef.current.contains(event.target)) {

        if (open) {

          setOpen(false);

          if (onBlur) {

            onBlur();

          }

        }

      }

    };



    document.addEventListener('mousedown', handleClickOutside);

    return () => {

      document.removeEventListener('mousedown', handleClickOutside);

    };

  }, [open, onBlur]);



  const selectedOption = options.find(option => option.value === value);

  const filteredOptions = options.filter(option =>

    option.label.toLowerCase().includes(search.toLowerCase())

  );



  return (

    <div className="relative" ref={containerRef}>

      {label && (

        <label className="block text-sm font-medium text-gray-700 mb-2">

          {label}

        </label>

      )}

      <button

        type="button"

        data-field={dataField}

        onBlur={() => onBlur && onBlur()}

        onClick={() => {

          if (!disabled) {

            setOpen(!open);

          }

        }}

        disabled={disabled}

        className={`w-full px-3 py-2 border rounded-lg flex items-center justify-between text-left ${touched && error ? 'border-red-500 bg-red-50' : 'border-gray-300'

          } ${disabled ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : 'bg-white'} focus:outline-none`}

      >

        <span className="truncate capitalize">

          {selectedOption ? selectedOption.label : placeholder}

        </span>

        <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />

      </button>

      {open && !disabled && (

        <div className="absolute z-30 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg">

          <div className="p-2 border-b border-gray-100">

            <input

              type="text"

              value={search}

              onChange={(e) => setSearch(e.target.value)}

              placeholder="Search..."

              className="input-field px-3 py-2 text-sm"

            />

          </div>

          <div className="max-h-64 overflow-y-auto">

            {filteredOptions.length === 0 ? (

              <div className="p-3 text-sm text-gray-500 text-center">

                No options found

              </div>

            ) : (

              filteredOptions.map(option => (

                <button

                  type="button"

                  key={option.value}

                  onClick={() => {

                    onChange(option.value);

                    setOpen(false);

                    setSearch('');

                  }}

                  className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50/30 ${option.value === value ? 'bg-gray-50 font-medium' : 'text-gray-700'

                    }`}

                >

                  <div className="truncate capitalize">{option.label}</div>

                  {option.meta && (

                    <div className="text-xs text-gray-500 capitalize">{option.meta}</div>

                  )}

                </button>

              ))

            )}

          </div>

        </div>

      )}

      {touched && error && (

        <p className="mt-1 text-sm text-red-600 flex items-center">

          <AlertCircle className="h-4 w-4 mr-1" />

          {error}

        </p>

      )}

    </div>

  );

};



const createInitialTabData = () => ({

  assignments: [],

  pagination: {},

  currentPage: 1,

  itemsPerPage: 10,

  loaded: false

});



const assigneeTypes = [

  { value: 'individual', label: 'Individual Users', icon: User },

  { value: 'department', label: 'Departments', icon: Building }

];



const courseStatusOptions = [

  { value: '', label: 'All Statuses' },

  { value: 'assigned', label: 'Assigned' },

  { value: 'in_progress', label: 'In Progress' },

  { value: 'completed', label: 'Completed' },

  { value: 'overdue', label: 'Overdue' }

];



const courseStatusMeta = {

  assigned: { label: 'Assigned', color: 'text-brand bg-brand-light bg-opacity-10' },

  in_progress: { label: 'In Progress', color: 'text-yellow-600 bg-yellow-100' },

  completed: { label: 'Completed', color: 'text-brand bg-blue-100' },

  overdue: { label: 'Overdue', color: 'text-red-600 bg-red-100' }

};



const quizStatusOptions = [

  { value: '', label: 'All Statuses' },

  { value: 'assigned', label: 'Assigned' },

  { value: 'in_progress', label: 'In Progress' },

  { value: 'completed', label: 'Completed' },

  { value: 'overdue', label: 'Overdue' }

];



const assignmentTabs = [

  { id: 'courses', label: 'Courses' },

  { id: 'quizzes', label: 'Quizzes' }

];



const AssignmentsEnrollment = () => {

  const navigate = useNavigate();

  const location = useLocation();



  // State Declarations

  const [activeAssignmentTab, setActiveAssignmentTab] = useState('courses');



  const defaultFilters = useMemo(() => ({

    page: 1,

    limit: 10,

    search: '',

    filterStatus: ''

  }), []);



  const {

    filters: assignmentFilters,

    searchTerm: searchQuery,

    setSearchTerm: setSearchQuery,

    updateFilter: updateAssignmentFilter,

    resetFilters: resetAssignmentFilters,

  } = useUrlTableState(defaultFilters);



  const searchTerm = searchQuery;

  const setSearchTerm = setSearchQuery;

  const debouncedSearchTerm = searchTerm; // Hook handles debouncing internally



  const selectedStatus = assignmentFilters.filterStatus || '';

  const setSelectedStatus = useCallback((status) => {

    updateAssignmentFilter({ filterStatus: status });

  }, [updateAssignmentFilter]);



  const searchInputRef = useRef(null);

  const [sortBy, setSortBy] = useState(null);

  const [sortOrder, setSortOrder] = useState(null);

  const [showCreateAssignment, setShowCreateAssignment] = useState(false);

  const [editingAssignment, setEditingAssignment] = useState(null);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [assignmentToDelete, setAssignmentToDelete] = useState(null);

  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState('');

  const [debouncedAssigneeSearchTerm, setDebouncedAssigneeSearchTerm] = useState('');



  // Assignment form state

  const [assignmentForm, setAssignmentForm] = useState({

    contentType: 'course',

    courseId: '',

    quizId: '',

    assigneeType: 'individual',

    assigneeIds: [],

    dueDate: '',

    sendEmail: true,

    allowSelfEnroll: false,

    notes: ''

  });



  const [formErrors, setFormErrors] = useState({});

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [selectedRows, setSelectedRows] = useState(new Map());

  const [showExportModal, setShowExportModal] = useState(false);

  const [exportLoading, setExportLoading] = useState(false);



  // Bulk Delete State

  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const [touchedFields, setTouchedFields] = useState(new Set());



  // Capitalize function for display values

  const capitalizeValue = (value) => {

    if (!value) return '';



    // Handle special cases

    const specialCases = {

      'soft_skills': 'Soft Skills',

      'all_employees': 'All Employees',

      'super_admin': 'Super Admin',

      'hr_manager': 'HR Manager',

      'hr_executive': 'HR Executive',

      'sales_manager': 'Sales Manager',

      'sales_executive': 'Sales Executive',

      'dispatch_manager': 'Dispatch Manager',

      'support_manager': 'Support Manager'

    };



    if (specialCases[value]) {

      return specialCases[value];

    }



    // Default capitalization

    return value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');

  };



  const formatDuration = (minutes) => {

    if (!minutes || minutes === 0) {

      return '0m';

    }

    const totalSeconds = Math.round(minutes * 60);

    const hours = Math.floor(totalSeconds / 3600);

    const mins = Math.floor((totalSeconds % 3600) / 60);

    const secs = totalSeconds % 60;



    const parts = [];

    if (hours > 0) parts.push(`${hours}h`);

    if (mins > 0) parts.push(`${mins}m`);

    if (secs > 0 && hours === 0) parts.push(`${secs}s`);



    return parts.length > 0 ? parts.join(' ') : '0m';

  };



  const clampPercentage = (value) => Math.min(100, Math.max(0, Math.round(value)));



  const getQuizAssigneeStatusClasses = (status) => {

    switch ((status || 'assigned').toLowerCase()) {

      case 'passed':

        return 'bg-blue-100 text-brand';

      case 'failed':

        return 'bg-red-100 text-red-700';

      case 'overdue':

        return 'bg-red-100 text-red-700';

      default:

        return 'bg-brand-light bg-opacity-10 text-brand';

    }

  };



  const getQuizAssignmentStatusMeta = (assignment, calculatedStatus) => {

    if (!assignment) {

      return { label: 'Assigned', color: 'bg-brand-light bg-opacity-10 text-brand' };

    }



    // Use the same status format as courses (assigned, in_progress, completed, overdue)

    const displayLabel = courseStatusMeta[calculatedStatus]?.label || capitalizeValue(calculatedStatus);

    const displayColor = courseStatusMeta[calculatedStatus]?.color || 'bg-brand-light bg-opacity-10 text-brand';



    return {

      label: displayLabel,

      color: displayColor

    };

  };



  const calculateAssigneeProgress = (assignee) => {

    if (!assignee) return 0;



    const completedLessons = typeof assignee.progress?.completedLessons === 'number'

      ? assignee.progress.completedLessons

      : null;

    const totalLessons = typeof assignee.progress?.totalLessons === 'number'

      ? assignee.progress.totalLessons

      : null;



    if (completedLessons !== null && totalLessons !== null && totalLessons > 0) {

      const progress = (Math.min(completedLessons, totalLessons) / totalLessons) * 100;

      return clampPercentage(progress);

    }



    if (typeof assignee.progress?.percentage === 'number') {

      return clampPercentage(assignee.progress.percentage);

    }



    if (typeof assignee.progress === 'number') {

      return clampPercentage(assignee.progress);

    }



    return 0;

  };



  const calculateAssigneeStatus = (assignee, assignmentDueDate) => {

    if (!assignee) return 'assigned';



    const progressValue = calculateAssigneeProgress(assignee);

    const progressStatus = assignee.progress?.status;

    const baseStatus = assignee.status;



    // For quizzes, 'passed' and 'failed' both mean completed (attempt was submitted)

    const isCompletedStatus = ['completed', 'passed', 'failed'].includes(progressStatus);

    const hasCompleted = progressValue >= 100 || baseStatus === 'completed' || baseStatus === 'passed' || baseStatus === 'failed' || isCompletedStatus;



    if (hasCompleted) {

      return 'completed';

    }



    const dueDate = assignmentDueDate ? new Date(assignmentDueDate) : null;

    const today = new Date();

    const isOverdue = dueDate && dueDate < today;



    if (isOverdue) {

      return 'overdue';

    }



    const hasProgress = progressValue > 0 || baseStatus === 'in_progress' || ['in_progress', 'started'].includes(progressStatus);

    if (hasProgress) {

      return 'in_progress';

    }



    return 'assigned';

  };



  const calculateAverageProgress = (assignment) => {

    if (!assignment) return 0;



    const assignees = Array.isArray(assignment.assignees) ? assignment.assignees : [];



    if (assignees.length > 0) {

      let totalCompletedLessons = 0;

      let totalLessonsAcrossAssignees = 0;

      const fallbackPercentages = [];



      assignees.forEach((assignee) => {

        const completedLessons = typeof assignee.progress?.completedLessons === 'number'

          ? assignee.progress.completedLessons

          : null;

        const totalLessons = typeof assignee.progress?.totalLessons === 'number'

          ? assignee.progress.totalLessons

          : null;



        if (completedLessons !== null && totalLessons !== null && totalLessons > 0) {

          totalCompletedLessons += Math.min(completedLessons, totalLessons);

          totalLessonsAcrossAssignees += totalLessons;

        } else {

          const percentage = calculateAssigneeProgress(assignee);

          fallbackPercentages.push(percentage);

        }

      });



      if (totalLessonsAcrossAssignees > 0) {

        const lessonBasedProgress = (totalCompletedLessons / totalLessonsAcrossAssignees) * 100;

        return clampPercentage(lessonBasedProgress);

      }



      if (fallbackPercentages.length > 0 && fallbackPercentages.length === assignees.length) {

        const percentageAvg = fallbackPercentages.reduce((sum, value) => sum + value, 0) / fallbackPercentages.length;

        return clampPercentage(percentageAvg);

      }

    }



    if (typeof assignment.progress === 'number') {

      return clampPercentage(assignment.progress);

    }



    return 0;

  };



  // Helper function to calculate days until due date

  const getDaysUntilDue = (dueDate) => {

    if (!dueDate) return null;

    const today = new Date();

    today.setHours(0, 0, 0, 0);

    const due = new Date(dueDate);

    due.setHours(0, 0, 0, 0);

    const diffTime = due - today;

    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;

  };



  // Helper function to calculate overall assignment status based on all assignees

  const calculateAssignmentStatus = (assignment) => {

    if (!assignment) return 'assigned';



    const assignees = Array.isArray(assignment.assignees) ? assignment.assignees : [];

    const statuses = assignees

      .map(assignee => calculateAssigneeStatus(assignee, assignment.dueDate))

      .filter(Boolean);

    const averageProgress = calculateAverageProgress(assignment);



    const allCompletedByStatus = statuses.length > 0 && statuses.every(s => s === 'completed');

    const fullyCompleted = averageProgress >= 100 || allCompletedByStatus;



    // Check if overdue based on due date

    const daysUntilDue = getDaysUntilDue(assignment.dueDate);

    const isOverdue = daysUntilDue !== null && daysUntilDue < 0;

    const hasOverdueStatus = statuses.some(s => s === 'overdue');

    const hasMeaningfulProgress = averageProgress > 0 || statuses.some(s => ['in_progress', 'completed'].includes(s));



    if (fullyCompleted) {

      return 'completed';

    } else if (isOverdue || hasOverdueStatus) {

      return 'overdue';

    } else if (hasMeaningfulProgress) {

      return 'in_progress';

    } else {

      return 'assigned';

    }

  };



  const getAssignmentContentType = (assignment) => (assignment?.contentType || 'course');

  const isQuizAssignment = (assignment) => getAssignmentContentType(assignment) === 'quiz';

  const getAssignmentContent = (assignment) => (isQuizAssignment(assignment) ? assignment.quiz : assignment.course);

  const getAssignmentTitle = (assignment) => {

    const content = getAssignmentContent(assignment);

    if (!content) {

      return isQuizAssignment(assignment) ? 'Untitled Quiz' : 'Untitled Course';

    }

    return content.title || (isQuizAssignment(assignment) ? 'Untitled Quiz' : 'Untitled Course');

  };

  const getAssignmentCategoryLabel = (assignment) => {

    if (isQuizAssignment(assignment)) {

      return null;

    }

    return capitalizeValue(assignment.course?.category) || 'General';

  };

  const getAssignmentDurationLabel = (assignment) => {

    if (isQuizAssignment(assignment)) {

      const timeLimit = assignment.quiz?.timeLimit;

      return timeLimit ? `${timeLimit} min` : 'N/A';

    }

    const duration = assignment.course?.duration;

    return duration ? formatDuration(Number(duration)) : 'N/A';

  };

  const getAssignmentIdentifier = (assignment) => {

    const content = getAssignmentContent(assignment);

    if (isQuizAssignment(assignment)) {

      return content?.quizId || content?._id || 'N/A';

    }

    return content?.courseId || 'N/A';

  };

  const getContentTypeFilter = (tabId = activeAssignmentTab) => (tabId === 'quizzes' ? 'quiz' : 'course');

  const handleAssignmentTabChange = (tabId) => {

    if (tabId === activeAssignmentTab) return;

    setActiveAssignmentTab(tabId);

  };





  // React Query Client

  const queryClient = useQueryClient();



  // Tab-specific pagination state

  const [paginationState, setPaginationState] = useState({

    courses: { page: 1, limit: 10 },

    quizzes: { page: 1, limit: 10 }

  });



  const currentPage = paginationState[activeAssignmentTab]?.page || 1;

  const itemsPerPage = paginationState[activeAssignmentTab]?.limit || 10;



  // Assignments Query

  const {

    data: assignmentsData,

    isLoading: assignmentsLoading,

    isError: assignmentsError

  } = useQuery({

    queryKey: ['training_assignments', activeAssignmentTab, currentPage, itemsPerPage, debouncedSearchTerm, selectedStatus],

    queryFn: async () => {

      const queryParams = {

        search: debouncedSearchTerm || '',

        status: selectedStatus || '',

        page: currentPage,

        limit: itemsPerPage,

        contentType: getContentTypeFilter(activeAssignmentTab)

      };



      const result = await trainingService.getAssignments(queryParams);

      if (!result.success) throw new Error(result.error);

      return result.data;

    },

    placeholderData: keepPreviousData

  });



  const assignments = assignmentsData?.assignments || [];

  const pagination = assignmentsData?.pagination || {};



  // Departments Query

  const { data: departments = [] } = useQuery({

    queryKey: ['training_departments'],

    queryFn: async () => {

      const response = await fetch(`${API_BASE_URL}/hr/departments?isActive=true&limit=1000&paginated=true`, {

        headers: {

          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,

          'Accept': 'application/json'

        }

      });

      const result = await response.json();



      // Handle various response formats (docs, departments, or direct array)

      const raw = result?.data?.docs || result?.data?.departments || result?.data || result?.departments || result || [];

      const departmentsList = Array.isArray(raw) ? raw : [];



      return departmentsList.map(dept => ({

        id: dept.name, // Keep using name as ID for backend compatibility if that's what was expected

        _id: dept._id || dept.id,

        name: dept.name || dept.departmentName || 'Unnamed Department',

        count: dept.employeeCount || 0 // Use employeeCount if available, else 0

      }));

    },

    staleTime: 0,

  });





  // Courses Query

  const { data: courses = [] } = useQuery({

    queryKey: ['training_courses', 'published'],

    queryFn: async () => {

      const result = await trainingService.getCourses({ status: 'published', limit: 1000 });

      if (!result.success) throw new Error(result.error);

      return Array.isArray(result.data?.courses) ? result.data.courses : (Array.isArray(result.data) ? result.data : []);

    }

  });



  // Quizzes Query

  const { data: quizzes = [] } = useQuery({

    queryKey: ['training_quizzes'],

    queryFn: async () => {

      const result = await trainingService.getQuizzes({ limit: 1000 });

      if (!result.success) throw new Error(result.error);

      return Array.isArray(result.data?.quizzes) ? result.data.quizzes : (Array.isArray(result.data) ? result.data : []);

    }

  });



  // Users Infinite Query

  const {

    data: usersData,

    fetchNextPage,

    hasNextPage,

    isLoading: initialLoadingUsers,

    isFetchingNextPage: loadingUsers,

    refetch: refetchUsers

  } = useInfiniteQuery({

    queryKey: ['training_users', assignmentForm.assigneeType, debouncedAssigneeSearchTerm],

    queryFn: async ({ pageParam = 1 }) => {

      if (assignmentForm.assigneeType !== 'individual') return { users: [], pagination: {} };



      const queryString = new URLSearchParams({

        page: pageParam.toString(),

        limit: USERS_PAGE_SIZE.toString(),

        ...(debouncedAssigneeSearchTerm ? { search: debouncedAssigneeSearchTerm } : {})

      });



      const response = await fetch(`${API_BASE_URL}/training-assignments/users?${queryString}`, {

        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }

      });

      const result = await response.json();

      if (result.responseCode !== 200) throw new Error(result.responseMessage);



      return {

        users: (result.data?.users || []).map(user => ({

          id: user._id,

          name: `${user.firstName} ${user.lastName}`.trim(),

          department: user.department,

          email: user.email,

          role: user.role,

          employeeId: user.employeeId

        })),

        pagination: result.data?.pagination

      };

    },

    getNextPageParam: (lastPage) => {

      return lastPage.pagination?.hasMore ? (lastPage.pagination.currentPage + 1) : undefined;

    },

    enabled: assignmentForm.assigneeType === 'individual'

  });



  const users = useMemo(() => {

    return usersData?.pages.flatMap(page => page.users) || [];

  }, [usersData]);



  // Reset form to initial state

  const resetForm = () => {

    setAssignmentForm({

      contentType: 'course',

      courseId: '',

      quizId: '',

      assigneeType: 'individual',

      assigneeIds: [],

      dueDate: '',

      sendEmail: true,

      allowSelfEnroll: false,

      notes: ''

    });

    setFormErrors({});

    setIsSubmitting(false);

    setTouchedFields(new Set());

    setAssigneeSearchTerm('');

  };



  // Mutations

  const createAssignmentMutation = useMutation({

    mutationFn: (data) => trainingService.createAssignment(data),

    onSuccess: () => {

      toast.success('Assignment created successfully');

      setShowCreateAssignment(false);

      resetForm();

      queryClient.invalidateQueries({ queryKey: ['training_assignments'] });

    },

    onError: (err) => {

      toast.error(err.message || 'Failed to create assignment');

    }

  });



  const updateAssignmentMutation = useMutation({

    mutationFn: ({ id, data }) => trainingService.updateAssignment(id, data),

    onSuccess: () => {

      toast.success('Assignment updated successfully');

      setEditingAssignment(null);

      resetForm();

      queryClient.invalidateQueries({ queryKey: ['training_assignments'] });

    },

    onError: (err) => {

      toast.error(err.message || 'Failed to update assignment');

    }

  });



  const deleteAssignmentMutation = useMutation({

    mutationFn: (id) => trainingService.deleteAssignment(id),

    onSuccess: () => {

      toast.success('Assignment deleted successfully');

      setShowDeleteConfirm(false);

      setAssignmentToDelete(null);

      queryClient.invalidateQueries({ queryKey: ['training_assignments'] });

    },

    onError: (err) => {

      toast.error(err.message || 'Failed to delete assignment');

    }

  });



  // Debounce search term - useUrlTableState handles debouncing internally for main search



  useEffect(() => {

    // Reset selection when tab changes to avoid confusion (optional, but consistent with paginated data if desired)

    // However, the user liked persistent cross-page selection, so let's keep it global or tab-specific.

    // For now, let's keep it global Map and not reset it here.

  }, [activeAssignmentTab]);



  useEffect(() => {

    const timer = setTimeout(() => {

      setDebouncedAssigneeSearchTerm(assigneeSearchTerm.trim());

    }, 400);

    return () => clearTimeout(timer);

  }, [assigneeSearchTerm]);



  // Handle pagination changes

  const handlePageChange = (newPage) => {

    setPaginationState(prev => ({

      ...prev,

      [activeAssignmentTab]: { ...prev[activeAssignmentTab], page: newPage }

    }));

  };



  const handleItemsPerPageChange = (newLimit) => {

    setPaginationState(prev => ({

      ...prev,

      [activeAssignmentTab]: { ...prev[activeAssignmentTab], limit: newLimit }

    }));

  };



  // Calculate Users from Infinite Query for dropdowns





  // Handle sort changes

  const handleSort = (field) => {

    if (sortBy === field) {

      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');

    } else {

      setSortBy(field);

      setSortOrder('asc');

    }

  };



  // Row selection handlers

  const handleSelectAll = (e) => {

    if (e.target.checked) {

      const newSelected = new Map(selectedRows);

      visibleAssignments?.forEach(asgn => {

        newSelected.set(asgn.id, asgn);

      });

      setSelectedRows(newSelected);

    } else {

      setSelectedRows(new Map());

    }

  };



  const handleSelectRow = (asgn) => {

    const newSelected = new Map(selectedRows);

    if (newSelected.has(asgn.id)) {

      newSelected.delete(asgn.id);

    } else {

      newSelected.set(asgn.id, asgn);

    }

    setSelectedRows(newSelected);

  };



  // --- Bulk Delete Handlers ---

  const handleBulkDelete = () => {

    if (selectedRows.size === 0) {

      toast.error('Please select at least one assignment to delete');

      return;

    }

    setShowBulkDeleteModal(true);

  };



  const confirmBulkDelete = async () => {

    setIsBulkDeleting(true);

    const ids = Array.from(selectedRows.keys());

    let successCount = 0;

    let errorCount = 0;



    for (const id of ids) {

      try {

        const result = await trainingService.deleteAssignment(id);

        if (result && result.success) {

          successCount++;

        } else {

          errorCount++;

        }

      } catch (err) {

        errorCount++;

      }

    }



    setIsBulkDeleting(false);

    setShowBulkDeleteModal(false);

    setSelectedRows(new Map());

    queryClient.invalidateQueries({ queryKey: ['training_assignments'] });



    if (successCount > 0) toast.success(`${successCount} assignment(s) deleted successfully`);

    if (errorCount > 0) toast.error(`Failed to delete ${errorCount} assignment(s)`);

  };



  const mapAssignmentsForExport = (asgns) => {

    return asgns.map(asgn => {

      const calculatedStatus = calculateAssignmentStatus(asgn);

      const statusMeta = isQuizAssignment(asgn)

        ? getQuizAssignmentStatusMeta(asgn, calculatedStatus)

        : { label: courseStatusMeta[calculatedStatus]?.label || capitalizeValue(calculatedStatus) };



      return {

        'Title': getAssignmentTitle(asgn),

        'Type': asgn.contentType === 'quiz' ? 'Quiz' : 'Course',

        'Assignees Count': (asgn.assignees || []).length,

        'Assignees': (asgn.users || []).map(u => `${u.firstName || ''} ${u.lastName || ''}`.trim()).join('; '),

        'Due Date': asgn.dueDate ? formatDate(asgn.dueDate, { includeTime: false }) : 'N/A',

        'Status': statusMeta.label,

        'Assigned By': asgn.assignedBy ? `${asgn.assignedBy.firstName || ''} ${asgn.assignedBy.lastName || ''}`.trim() : 'N/A',

        'Assigned Date': asgn.assignedDate ? formatDate(asgn.assignedDate, { includeTime: true }) : 'N/A',

        'Notes': asgn.notes || ''

      };

    });

  };



  const handleExportClick = () => {

    if (selectedRows.size > 0) {

      const asgnsToExport = Array.from(selectedRows.values());

      const exportData = mapAssignmentsForExport(asgnsToExport);

      exportToCSV(exportData, 'assignments', {

        title: 'Assignments & Enrollment List',

        type: 'Selected Assignments'

      });

    } else {

      setShowExportModal(true);

    }

  };



  const handleExportCurrentPage = () => {

    const exportData = mapAssignmentsForExport(visibleAssignments);

    exportToCSV(exportData, 'assignments_page', {

      title: 'Assignments & Enrollment List',

      type: 'Current Page'

    });

    setShowExportModal(false);

  };



  const handleExportRange = async (startDate, endDate) => {

    setExportLoading(true);

    try {

      const queryParams = new URLSearchParams();

      queryParams.append('type', `Date Range (${startDate} to ${endDate})`);

      if (startDate) queryParams.append('startDate', startDate);

      if (endDate) queryParams.append('endDate', endDate);

      if (debouncedSearchTerm) queryParams.append('search', debouncedSearchTerm);

      queryParams.append('contentType', getContentTypeFilter(activeAssignmentTab));



      const url = `${API_BASE_URL}/training-assignments/export?${queryParams.toString()}`;

      const filename = generateExportFilename('assignments_range');

      await downloadFromBackend(url, filename);

      setShowExportModal(false);

      toast.success('Export started successfully');

    } catch (error) {



      toast.error('An error occurred during export');

    } finally {

      setExportLoading(false);

    }

  };



  const handleExportAll = async () => {

    setExportLoading(true);

    try {

      const queryParams = new URLSearchParams();

      queryParams.append('type', 'All Data');

      if (debouncedSearchTerm) queryParams.append('search', debouncedSearchTerm);

      queryParams.append('contentType', getContentTypeFilter(activeAssignmentTab));



      const url = `${API_BASE_URL}/training-assignments/export?${queryParams.toString()}`;

      const filename = generateExportFilename('assignments_all');

      await downloadFromBackend(url, filename);

      setShowExportModal(false);

      toast.success('Export started successfully');

    } catch (error) {



      toast.error('An error occurred during export');

    } finally {

      setExportLoading(false);

    }

  };



  // Frontend Sorting implementation

  const sortedAssignments = useMemo(() => {

    if (!assignments) return [];



    // Create a new array to avoid mutating the original

    const sortableAssignments = [...assignments];



    if (sortBy) {

      sortableAssignments.sort((a, b) => {

        let aValue, bValue;



        switch (sortBy) {

          case 'title':

            aValue = getAssignmentTitle(a).toLowerCase();

            bValue = getAssignmentTitle(b).toLowerCase();

            break;

          case 'assignees':

            aValue = (a.assignees || []).length;

            bValue = (b.assignees || []).length;

            break;

          case 'dueDate':

            aValue = new Date(a.dueDate).getTime();

            bValue = new Date(b.dueDate).getTime();

            break;

          case 'status':

            // Logic to sort by status priority or alphabetical

            aValue = calculateAssignmentStatus(a);

            bValue = calculateAssignmentStatus(b);

            break;

          case 'assignedDate':

            aValue = new Date(a.assignedDate || a.createdAt || 0).getTime();

            bValue = new Date(b.assignedDate || b.createdAt || 0).getTime();

            break;

          default:

            return 0;

        }



        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;

        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;

        return 0;

      });

    }



    return sortableAssignments;

  }, [assignments, sortBy, sortOrder]);



  // Alias sorted assignments as visibleAssignments for compatibility

  // Note: Pagination is now handled by the backend/Query, so 'visible' just means 'current page data sorted'

  const visibleAssignments = sortedAssignments;

  const loading = assignmentsLoading; // Alias for compatibility with legacy UI checks





  // Form validation function

  const validateForm = () => {

    const errors = {};



    if (assignmentForm.contentType === 'quiz') {

      if (!assignmentForm.quizId) {

        errors.quizId = 'Quiz is required';

      }

    } else if (!assignmentForm.courseId) {

      errors.courseId = 'Course is required';

    }



    if (!assignmentForm.assigneeIds || assignmentForm.assigneeIds.length === 0) {

      errors.assigneeIds = 'At least one assignee must be selected';

    }



    // Validate dueDate (required)

    if (!assignmentForm.dueDate || assignmentForm.dueDate === '') {

      errors.dueDate = 'Due date is required';

    } else {

      const today = new Date().toISOString().split('T')[0];

      if (assignmentForm.dueDate < today) {

        errors.dueDate = 'Due date cannot be in the past';

      }

    }



    // Validate notes (optional)

    if (assignmentForm.notes && assignmentForm.notes.trim().length > 3000) {

      errors.notes = 'Notes cannot exceed 3000 characters';

    } else if (assignmentForm.notes && !/^[a-zA-Z0-9\s\-_&().,;:!?'"]*$/.test(assignmentForm.notes)) {

      errors.notes = 'Notes contains invalid characters. Only letters, numbers, spaces, and basic punctuation are allowed';

    }



    setFormErrors(errors);

    return Object.keys(errors).length === 0;

  };



  // Handle field blur to mark as touched

  const handleFieldBlur = (fieldName) => {

    setTouchedFields(prev => new Set([...prev, fieldName]));

    // Validate the field on blur

    validateForm();

  };



  const handleCreateAssignment = () => {

    setAssignmentForm({

      contentType: 'course',

      courseId: '',

      quizId: '',

      assigneeType: 'individual',

      assigneeIds: [],

      dueDate: '',

      sendEmail: true,

      allowSelfEnroll: false,

      notes: ''

    });

    setFormErrors({});

    setTouchedFields(new Set());

    setShowCreateAssignment(true);

    setEditingAssignment(null);

  };



  const handleEditAssignment = (assignment) => {

    setEditingAssignment(assignment);



    // Use stored assignee type and selection if available, otherwise fall back to individual

    const assigneeType = assignment.assigneeType || 'individual';

    const assigneeIds = assignment.assigneeSelection || (assignment.assignees || []).map(a => {

      return a.user?._id || a.user?.id || a.id || a._id;

    }).filter(id => id);



    const assignmentContentType = assignment.contentType || 'course';

    setAssignmentForm({

      contentType: assignmentContentType,

      courseId: assignmentContentType === 'course'

        ? (assignment.course?.id || assignment.course?._id || '')

        : '',

      quizId: assignmentContentType === 'quiz'

        ? (assignment.quiz?.id || assignment.quiz?._id || assignment.quiz?.quizId || '')

        : '',

      assigneeType: assigneeType,

      assigneeIds: assigneeIds,

      dueDate: assignment.dueDate ? new Date(assignment.dueDate).toISOString().split('T')[0] : '',

      sendEmail: assignment.sendEmail !== undefined ? assignment.sendEmail : true,

      allowSelfEnroll: assignment.allowSelfEnroll || false,

      notes: assignment.notes || ''

    });

    setFormErrors({});

    setTouchedFields(new Set());

    setShowCreateAssignment(true);

  };



  const handleSaveAssignment = async () => {

    // Mark all required fields as touched on submit

    const fieldsToTouch = assignmentForm.contentType === 'quiz'

      ? ['quizId', 'assigneeIds', 'dueDate', 'notes']

      : ['courseId', 'assigneeIds', 'dueDate', 'notes'];

    setTouchedFields(new Set(fieldsToTouch));



    // Validate form before saving - simplified

    const errors = {};



    // Check required fields

    if (assignmentForm.contentType === 'quiz') {

      if (!assignmentForm.quizId || assignmentForm.quizId === '') {

        errors.quizId = 'Quiz is required';

      }

    } else if (!assignmentForm.courseId || assignmentForm.courseId === '') {

      errors.courseId = 'Course is required';

    }



    if (!assignmentForm.assigneeIds || assignmentForm.assigneeIds.length === 0) {

      errors.assigneeIds = 'At least one assignee must be selected';

    }



    // Validate dueDate (required)

    if (!assignmentForm.dueDate || assignmentForm.dueDate === '') {

      errors.dueDate = 'Due date is required';

    } else {

      const today = new Date().toISOString().split('T')[0];

      if (assignmentForm.dueDate < today) {

        errors.dueDate = 'Due date cannot be in the past';

      }

    }



    // Validate notes (optional)

    if (assignmentForm.notes && assignmentForm.notes.trim().length > 3000) {

      errors.notes = 'Notes cannot exceed 3000 characters';

    } else if (assignmentForm.notes && !/^[a-zA-Z0-9\s\-_&().,;:!?'"]*$/.test(assignmentForm.notes)) {

      errors.notes = 'Notes contains invalid characters';

    }



    // Set errors immediately

    setFormErrors(errors);



    // Prevent submission if there are any errors

    if (Object.keys(errors).length > 0) {

      // Scroll to first error

      const firstErrorField = Object.keys(errors)[0];

      setTimeout(() => {

        const errorElement = document.querySelector(`[data-field="${firstErrorField}"]`);

        if (errorElement) {

          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

          errorElement.focus();

        }

      }, 100);

      return;

    }



    setIsSubmitting(true);



    try {

      // Convert dueDate to ISO string format

      const dueDate = new Date(assignmentForm.dueDate).toISOString();



      // Prepare assignment data - simplified for production

      const assignmentData = {

        contentType: assignmentForm.contentType,

        assigneeType: assignmentForm.assigneeType,

        assigneeIds: assignmentForm.assigneeIds,

        dueDate: dueDate,

        sendEmail: assignmentForm.sendEmail,

        allowSelfEnroll: assignmentForm.allowSelfEnroll,

        notes: assignmentForm.notes || ''

      };



      if (assignmentForm.contentType === 'quiz') {

        assignmentData.quiz = assignmentForm.quizId;

      } else {

        assignmentData.course = assignmentForm.courseId;

      }



      if (editingAssignment) {

        updateAssignmentMutation.mutate({ id: editingAssignment.id, data: assignmentData });

      } else {

        createAssignmentMutation.mutate(assignmentData);

      }

    } catch (error) {



      toast.error('Failed to save assignment');

      setIsSubmitting(false);

    }

  };



  const getAssigneeDetails = (type, ids) => {

    switch (type) {

      case 'individual':

        return (users || []).filter(u => ids.includes(u.id));

      case 'department':

        return (departments || []).filter(d => ids.includes(d.id));

      default:

        return [];

    }

  };



  const handleDeleteAssignment = (assignmentId) => {

    // Set the assignment to delete and show confirmation modal

    setAssignmentToDelete(assignmentId);

    setShowDeleteConfirm(true);

  };



  const confirmDeleteAssignment = async () => {

    if (assignmentToDelete) {

      deleteAssignmentMutation.mutate(assignmentToDelete);

    }

  };





  const cancelDeleteAssignment = () => {

    setShowDeleteConfirm(false);

    setAssignmentToDelete(null);

  };



  const formatAssigneeCount = (assignment) => {

    const assigneeCount = Array.isArray(assignment?.assignees) ? assignment.assignees.length : 0;

    if (assigneeCount === 1) return '1 assignee';

    return `${assigneeCount} assignees`;

  };



  const getAssignmentStatusLabel = (assignment) => {

    const statusValue = calculateAssignmentStatus(assignment);

    if (activeAssignmentTab === 'quizzes') {

      return getQuizAssignmentStatusMeta(assignment, statusValue).label;

    }

    return courseStatusMeta[statusValue]?.label || capitalizeValue(statusValue);

  };



  const handleExport = () => {

    try {

      const assignmentsToExport = visibleAssignments || [];

      const csvContent = [

        ['Title', 'Assignees', 'Due Date', 'Status'],

        ...assignmentsToExport.map((assignment) => [

          getAssignmentTitle(assignment),

          formatAssigneeCount(assignment),

          assignment.dueDate ? formatDate(assignment.dueDate) : 'N/A',

          getAssignmentStatusLabel(assignment)

        ])

      ]

        .map((row) =>

          row

            .map((value) => {

              const cell = value ?? '';

              return typeof cell === 'string' && cell.includes(',')

                ? `"${cell.replace(/"/g, '""')}"`

                : cell;

            })

            .join(',')

        )

        .join('\n');



      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

      const link = document.createElement('a');

      link.href = URL.createObjectURL(blob);

      link.download = `assignments_${activeAssignmentTab}_${new Date().toISOString().split('T')[0]}.csv`;

      link.click();

    } catch (error) {



    }

  };



  const handleClearFilters = () => {

    resetAssignmentFilters();

    setPaginationState(prev => ({

      ...prev,

      [activeAssignmentTab]: { ...prev[activeAssignmentTab], page: 1 }

    }));

  };







  // Handle editing from navigation state

  useEffect(() => {

    if (location.state?.editingAssignmentId && assignmentsData?.assignments) {

      const assignmentToEdit = assignmentsData.assignments.find(

        a => String(a.id) === String(location.state.editingAssignmentId)

      );

      if (assignmentToEdit) {

        handleEditAssignment(assignmentToEdit);

        // Clear state to avoid reopening on refresh

        navigate(location.pathname, { replace: true, state: {} });

      }

    }

  }, [location.state, assignmentsData, handleEditAssignment, navigate]);





  // Check if data is loaded

  if (assignmentsLoading) {

    return (

      <div className="flex items-center justify-center h-64">

        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>

      </div>

    );

  }



  return (

    <div className="space-y-6">

      {/* Toast Notification */}

      <>

        {/* Header */}

        <div className="flex items-center justify-between">

          <div>

            <h1 className="text-2xl font-bold text-gray-900">Assignments & Enrollment</h1>

            <p className="text-gray-600 mt-1">Assign training courses to individual users or departments and manage enrollments</p>

          </div>



          <div className="flex items-center space-x-4">

            {selectedRows.size > 0 && (

              <PermissionGate permission="training" action="delete">

                <button

                  onClick={handleBulkDelete}

                  className="flex items-center btn-primary"

                >

                  <Trash2 className="h-4 w-4 mr-2" />

                  Delete ({selectedRows.size})

                </button>

              </PermissionGate>

            )}

            <PermissionGate permission="training" action="read">

              <Button

                variant={selectedRows.size > 0 ? "primary" : "outline"}

                icon={<Download className="h-4 w-4" />}

                onClick={handleExportClick}

                loading={exportLoading}

              >

                {selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : 'Export'}

              </Button>

            </PermissionGate>

            <PermissionGate permission="training" action="create">

              <button

                onClick={handleCreateAssignment}

                className="flex items-center btn-create"

              >

                <Plus className="h-5 w-5 mr-2" />

                Create Assignment

              </button>

            </PermissionGate>

          </div>

        </div>



        {/* Assignment Type Tabs */}

        <div className="flex items-center space-x-3 mt-4">

          {assignmentTabs.map((tab) => (

            <button

              key={tab.id}

              onClick={() => handleAssignmentTabChange(tab.id)}

              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${activeAssignmentTab === tab.id

                ? 'btn-create shadow'

                : 'btn-secondary opacity-60 hover:opacity-100'

                }`}

            >

              {tab.label}

            </button>

          ))}

        </div>



        {/* Search and Filters */}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">

          <div className="flex flex-wrap items-end gap-4 w-full">

            <div className="relative flex-[2] min-w-[200px]">

              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />

              <input

                ref={searchInputRef}

                type="text"

                placeholder="Search by Title..."

                value={searchTerm}

                onChange={(e) => {

                  // Allow alphanumeric, spaces, and common search/document symbols

                  const value = e.target.value.replace(/[^a-zA-Z0-9\s,.@\/\-_\(\)\[\]#:&?!]/g, '').slice(0, 100);

                  setSearchTerm(value);

                }}

                onKeyPress={(e) => {

                  // Allow alphanumeric, spaces, and common search/document symbols

                  if (!/^[a-zA-Z0-9\s,.@\/\-_\(\)\[\]#:&?!]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {

                    e.preventDefault();

                  }

                }}

                maxLength={100}

                className="input-field pl-10 pr-10 h-[42px]"

                autoComplete="off"

              />

              {searchTerm && (

                <button

                  onClick={() => {

                    setSearchTerm('');

                    if (searchInputRef.current) {

                      searchInputRef.current.focus();

                    }

                  }}

                  className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"

                >

                  <X className="h-4 w-4" />

                </button>

              )}

            </div>



            <div className="flex-shrink-0">

              <select

                value={selectedStatus}

                onChange={(e) => {

                  setSelectedStatus(e.target.value);

                  setPaginationState(prev => {

                    const next = { ...prev };

                    next[activeAssignmentTab] = {

                      ...next[activeAssignmentTab],

                      page: 1

                    };

                    return next;

                  });

                }}

                className="input-field px-3 h-[42px]"

              >

                {(activeAssignmentTab === 'quizzes' ? quizStatusOptions : courseStatusOptions).map((status) => (

                  <option key={status.value} value={status.value}>

                    {status.label}

                  </option>

                ))}

              </select>

            </div>



            {(searchTerm || selectedStatus) && (

              <div className="flex-shrink-0">

                <PermissionGate permission="training" action="read">

                  <Button

                    variant="outline"

                    className="h-[42px]"

                    icon={<X className="h-4 w-4" />}

                    onClick={handleClearFilters}

                  >

                    Clear

                  </Button>

                </PermissionGate>

              </div>

            )}

          </div>

        </div>



        {/* Assignments List/Grid */}

        {!assignmentsLoading && visibleAssignments.length === 0 ? (

          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">

            <div className="flex flex-col items-center justify-center">

              <BookOpen className="h-16 w-16 text-gray-400 mb-4" />

              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Assignments Found</h3>

              <p className="text-gray-500 mb-4">

                {searchTerm || selectedStatus

                  ? 'No assignments match your current filters. Try adjusting your search or filter criteria.'

                  : 'There are no assignments available at this time.'}

              </p>

              {(searchTerm || selectedStatus) && (

                <PermissionGate permission="training" action="read">

                  <button

                    onClick={handleClearFilters}

                    className="btn-create"

                  >

                    Clear Filters

                  </button>

                </PermissionGate>

              )}

            </div>

          </div>

        ) : (

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">

            <div className="overflow-x-auto">

              <table className="w-full divide-y divide-gray-200 table-fixed">

                <colgroup>

                  <col style={{ width: '4%' }} />

                  <col style={{ width: '22%' }} />

                  <col style={{ width: '14%' }} />

                  <col style={{ width: '15%' }} />

                  <col style={{ width: '15%' }} />

                  <col style={{ width: '12%' }} />

                  <col style={{ width: '18%' }} />

                </colgroup>

                <thead className="bg-transparent" style={{ backgroundColor: 'rgba(59, 130, 246, 0.14)' }}>

                  <tr>

                    <th className="px-4 py-3 text-left">

                      <input

                        type="checkbox"

                        className="rounded border-gray-300 text-[#0F2942] focus:ring-[#0F2942]/50 h-3 w-3"

                        checked={visibleAssignments?.length > 0 && visibleAssignments.every(asgn => selectedRows.has(asgn.id))}

                        onChange={handleSelectAll}

                      />

                    </th>

                    <th

                      className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-50/30"

                      onClick={() => handleSort('title')}

                    >

                      <div className="flex items-center space-x-1">

                        <span>Title</span>

                        {sortBy === 'title' ? (

                          sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-[#0F2942]" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-[#0F2942]" />

                        ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}

                      </div>

                    </th>

                    <th

                      className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-blue-50/30"

                      onClick={() => handleSort('assignees')}

                    >

                      <div className="flex items-center space-x-1">

                        <span>Assignees</span>

                        {sortBy === 'assignees' ? (

                          sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-[#0F2942]" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-[#0F2942]" />

                        ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}

                      </div>

                    </th>

                    <th

                      className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-blue-50/30"

                      onClick={() => handleSort('dueDate')}

                    >

                      <div className="flex items-center space-x-1">

                        <span>Due Date</span>

                        {sortBy === 'dueDate' ? (

                          sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-[#0F2942]" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-[#0F2942]" />

                        ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}

                      </div>

                    </th>

                    <th

                      className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-blue-50/30"

                      onClick={() => handleSort('status')}

                    >

                      <div className="flex items-center space-x-1">

                        <span>Status</span>

                        {sortBy === 'status' ? (

                          sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-[#0F2942]" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-[#0F2942]" />

                        ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}

                      </div>

                    </th>

                    <th

                      className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-blue-50/30"

                      onClick={() => handleSort('assignedDate')}

                    >

                      <div className="flex items-center space-x-1">

                        <span>Assigned Date</span>

                        {sortBy === 'assignedDate' ? (

                          sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-[#0F2942]" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-[#0F2942]" />

                        ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}

                      </div>

                    </th>

                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider whitespace-nowrap">Actions</th>

                  </tr>

                </thead>

                <tbody className="bg-white divide-y divide-gray-100">

                  {sortedAssignments.map((assignment) => {

                    const calculatedStatus = calculateAssignmentStatus(assignment);

                    const statusMeta = isQuizAssignment(assignment)

                      ? getQuizAssignmentStatusMeta(assignment, calculatedStatus)

                      : { label: courseStatusMeta[calculatedStatus]?.label || capitalizeValue(calculatedStatus), color: courseStatusMeta[calculatedStatus]?.color || 'bg-brand-light bg-opacity-10 text-brand' };



                    return (

                      <tr key={assignment.id} className="hover:bg-blue-50/30 transition-colors">

                        <td className="px-4 py-4 text-center">

                          <input

                            type="checkbox"

                            className="rounded border-gray-300 text-[#0F2942] focus:ring-[#0F2942]/50 h-3 w-3"

                            checked={selectedRows.has(assignment.id)}

                            onChange={() => handleSelectRow(assignment)}

                          />

                        </td>

                        <td className="px-4 py-4">

                          <div className="space-y-1">

                            <p

                              className="text-sm font-semibold text-gray-900 truncate capitalize"

                              title={getAssignmentTitle(assignment)}

                            >

                              {getAssignmentTitle(assignment)}

                            </p>

                          </div>

                        </td>

                        <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">

                          {(assignment.assignees || []).length} assignee

                          {(assignment.assignees || []).length === 1 ? '' : 's'}

                        </td>

                        <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">

                          {assignment.dueDate ? formatDate(assignment.dueDate, { includeTime: false }) : 'N/A'}

                        </td>

                        <td className="px-4 py-4 text-sm whitespace-nowrap">

                          {statusMeta ? (

                            <span

                              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium capitalize ${statusMeta.color}`}

                            >

                              {statusMeta.label}

                            </span>

                          ) : (

                            <span className="text-gray-400 text-sm">--</span>

                          )}

                        </td>

                        <td className="px-4 py-4 text-sm text-gray-500 whitespace-nowrap">

                          {assignment.assignedDate ? formatDate(assignment.assignedDate, { includeTime: true }) : (assignment.createdAt ? formatDate(assignment.createdAt, { includeTime: true }) : 'N/A')}

                        </td>

                        <td className="px-4 py-4 text-right whitespace-nowrap">

                          <div className="flex items-center justify-end space-x-2">

                            <PermissionGate permission="training" action="read">

                              <button

                                onClick={() => navigate(`/hrms/training/assignment/${assignment.id}`)}

                                className="p-2 text-brand hover:bg-blue-50/20 rounded-lg transition-colors"

                                title="View Assignment Details"

                              >

                                <Eye className="h-4 w-4" />

                              </button>

                            </PermissionGate>

                            <PermissionGate permission="training" action="update">

                              <button

                                onClick={() => handleEditAssignment(assignment)}

                                className="p-2 text-[#0F2942] hover:bg-blue-50 rounded-lg transition-colors"

                                title="Edit Assignment"

                              >

                                <Edit className="h-4 w-4" />

                              </button>

                            </PermissionGate>

                            <PermissionGate permission="training" action="delete">

                              <button

                                onClick={() => handleDeleteAssignment(assignment.id)}

                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"

                                title="Delete Assignment"

                              >

                                <Trash2 className="h-4 w-4" />

                              </button>

                            </PermissionGate>

                          </div>

                        </td>

                      </tr>

                    );

                  })}

                </tbody>

              </table>
            </div>

            {/* Pagination */}
            {!assignmentsLoading && assignmentsError && (
              <div className="p-4 text-center text-red-600">
                Failed to load assignments. Please try again.
              </div>
            )}
            {!assignmentsLoading &&
              pagination &&
              typeof pagination.totalAssignments === 'number' &&
              pagination.totalAssignments > 0 &&
              visibleAssignments.length > 0 && (
                <PaginationControls
                  currentPage={pagination.currentPage || 1}
                  totalPages={pagination.totalPages || 1}
                  totalItems={pagination.totalAssignments}
                  limit={pagination.limit || itemsPerPage}
                  hasNextPage={pagination.hasNextPage !== false && (pagination.currentPage || 1) < (pagination.totalPages || 1)}
                  hasPrevPage={pagination.hasPrevPage !== false && (pagination.currentPage || 1) > 1}
                  onPageChange={handlePageChange}
                  onLimitChange={handleItemsPerPageChange}
                  border={false}
                />
              )}
          </div>
        )}

      </>



      {/* Create/Edit Assignment Modal */}

      {showCreateAssignment && (

        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">

          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[95vh] overflow-y-auto">

            <div className="p-6 border-b border-gray-200">

              <h2 className="text-xl font-semibold text-gray-900">

                {editingAssignment ? 'Edit Assignment' : 'Create Assignment'}

              </h2>

            </div>



            <div className="p-6">

              <div className="space-y-6">

                {/* Assignment Content Selection */}

                <div className="space-y-4">

                  <div>

                    <label className="block text-sm font-medium text-gray-700 mb-2">Assignment Type *</label>

                    <div className="flex items-center gap-3">

                      {['course', 'quiz'].map(type => (

                        <button

                          key={type}

                          type="button"

                          onClick={() => {

                            setAssignmentForm(prev => ({

                              ...prev,

                              contentType: type,

                              courseId: type === 'course' ? prev.courseId : '',

                              quizId: type === 'quiz' ? prev.quizId : ''

                            }));

                            if (type === 'course') {

                              setFormErrors(prev => ({ ...prev, quizId: null }));

                            } else {

                              setFormErrors(prev => ({ ...prev, courseId: null }));

                            }

                          }}

                          className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${assignmentForm.contentType === type

                            ? 'border-brand bg-brand-light bg-opacity-10 text-brand'

                            : 'border-gray-300 text-gray-600 hover:border-gray-400'

                            }`}

                        >

                          {type === 'course' ? 'Course' : 'Quiz'}

                        </button>

                      ))}

                    </div>

                  </div>



                  {assignmentForm.contentType === 'course' ? (

                    <SearchableSingleSelect

                      label="Course *"

                      value={assignmentForm.courseId}

                      onChange={(value) => {

                        setAssignmentForm(prev => ({ ...prev, courseId: value }));

                        if (formErrors.courseId) {

                          setFormErrors(prev => ({ ...prev, courseId: null }));

                        }

                      }}

                      onBlur={() => handleFieldBlur('courseId')}

                      options={(courses || []).map(course => ({

                        value: course._id || course.id,

                        label: course.title

                      }))}

                      placeholder="Search course..."

                      error={formErrors.courseId}

                      touched={touchedFields.has('courseId')}

                      dataField="courseId"

                    />

                  ) : (

                    <SearchableSingleSelect

                      label="Quiz *"

                      value={assignmentForm.quizId}

                      onChange={(value) => {

                        setAssignmentForm(prev => ({ ...prev, quizId: value }));

                        if (formErrors.quizId) {

                          setFormErrors(prev => ({ ...prev, quizId: null }));

                        }

                      }}

                      onBlur={() => handleFieldBlur('quizId')}

                      options={(quizzes || []).map(quiz => ({

                        value: quiz._id || quiz.id,

                        label: quiz.title,

                        meta: capitalizeValue(quiz.quizType) || 'Quiz'

                      }))}

                      placeholder="Search quiz..."

                      error={formErrors.quizId}

                      touched={touchedFields.has('quizId')}

                      dataField="quizId"

                      disabled={(quizzes || []).length === 0}

                    />

                  )}

                </div>



                {/* Assignee Type */}

                <div>

                  <label className="block text-sm font-medium text-gray-700 mb-2">Assignee Type *</label>

                  <div className="grid grid-cols-3 gap-3">

                    {assigneeTypes.map(type => (

                      <button

                        key={type.value}

                        onClick={() => {

                          setAssignmentForm(prev => ({ ...prev, assigneeType: type.value, assigneeIds: [] }));

                          setAssigneeSearchTerm('');

                        }}

                        className={`p-3 border rounded-lg text-left transition-colors ${assignmentForm.assigneeType === type.value

                          ? 'border-brand bg-brand-light bg-opacity-10 text-brand'

                          : 'border-gray-300 hover:border-gray-400'

                          }`}

                      >

                        <type.icon className="h-5 w-5 mb-2" />

                        <div className="text-sm font-medium">{type.label}</div>

                      </button>

                    ))}

                  </div>

                </div>



                {/* Assignee Selection */}

                <div>

                  <div className="flex items-center justify-between mb-2">

                    <label className="block text-sm font-medium text-gray-700">Select Assignees *</label>

                    {assignmentForm.assigneeType === 'individual' && users && users.length > 0 && (

                      <div className="flex space-x-2">

                        <button

                          type="button"

                          onClick={() => {

                            const allUserIds = users.map(user => user.id);

                            setAssignmentForm(prev => ({ ...prev, assigneeIds: allUserIds }));

                          }}

                          className="text-xs text-[#0F2942] hover:text-blue-800"

                        >

                          Select All

                        </button>

                        <button

                          type="button"

                          onClick={() => setAssignmentForm(prev => ({ ...prev, assigneeIds: [] }))}

                          className="text-xs text-gray-600 hover:text-gray-800"

                        >

                          Clear All

                        </button>

                      </div>

                    )}

                    {assignmentForm.assigneeType === 'department' && departments && departments.length > 0 && (

                      <div className="flex space-x-2">

                        <button

                          type="button"

                          onClick={() => {

                            const allDeptIds = departments.map(dept => dept.id);

                            setAssignmentForm(prev => ({ ...prev, assigneeIds: allDeptIds }));

                          }}

                          className="text-xs text-[#0F2942] hover:text-blue-800"

                        >

                          Select All

                        </button>

                        <button

                          type="button"

                          onClick={() => setAssignmentForm(prev => ({ ...prev, assigneeIds: [] }))}

                          className="text-xs text-gray-600 hover:text-gray-800"

                        >

                          Clear All

                        </button>

                      </div>

                    )}

                  </div>

                  <div

                    data-field="assigneeIds"

                    onBlur={(e) => {

                      // Only trigger blur if focus is leaving the container (not moving to a checkbox inside)

                      if (!e.currentTarget.contains(e.relatedTarget)) {

                        handleFieldBlur('assigneeIds');

                      }

                    }}

                    className={`max-h-40 overflow-y-auto border rounded-lg p-3 ${touchedFields.has('assigneeIds') && formErrors.assigneeIds ? 'border-red-500 bg-red-50' : 'border-gray-300'

                      }`}

                  >

                    {assignmentForm.assigneeType === 'individual' && (

                      <div className="space-y-2">

                        <div className="mb-3 flex space-x-2">

                          <input

                            type="text"

                            placeholder="Search users..."

                            value={assigneeSearchTerm}

                            onChange={(e) => {

                              // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)

                              const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);

                              setAssigneeSearchTerm(value);

                            }}

                            onKeyPress={(e) => {

                              // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)

                              if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {

                                e.preventDefault();

                              }

                            }}

                            maxLength={100}

                            className="flex-1 input-field px-3 py-2 text-sm"

                          />

                          <button

                            type="button"

                            onClick={() => refetchUsers()}

                            disabled={initialLoadingUsers || loadingUsers}

                            className="btn-secondary px-3 py-2 text-sm disabled:opacity-50"

                          >

                            {initialLoadingUsers || loadingUsers ? '...' : '↻'}

                          </button>

                        </div>

                        {initialLoadingUsers ? (

                          <div className="text-sm text-gray-500 py-2">Loading users...</div>

                        ) : users && users.length > 0 ? (

                          <>

                            {users.map(user => (

                              <label key={user.id} className="flex items-center space-x-2 cursor-pointer hover:bg-blue-50/30 p-1 rounded">

                                <input

                                  type="checkbox"

                                  checked={assignmentForm.assigneeIds.includes(user.id)}

                                  onChange={(e) => {

                                    if (e.target.checked) {

                                      setAssignmentForm(prev => ({

                                        ...prev,

                                        assigneeIds: [...prev.assigneeIds, user.id]

                                      }));

                                    } else {

                                      setAssignmentForm(prev => ({

                                        ...prev,

                                        assigneeIds: prev.assigneeIds.filter(id => id !== user.id)

                                      }));

                                    }

                                  }}

                                  className="h-4 w-4 text-brand focus:ring-0 border-gray-300 rounded"

                                />

                                <span className="text-sm capitalize">

                                  {user.name} {user.employeeId ? `(${user.employeeId})` : ''} - {user.department} ({user.role})

                                  <span className="block text-xs text-gray-400 lowercase">{user.email}</span>

                                </span>

                              </label>

                            ))}

                            {hasNextPage && (

                              <button

                                type="button"

                                onClick={() => fetchNextPage()}

                                disabled={loadingUsers}

                                className="mt-2 w-full text-center text-sm text-[#0F2942] hover:text-blue-800"

                              >

                                {loadingUsers ? 'Loading...' : 'Load more'}

                              </button>

                            )}

                          </>

                        ) : (

                          <div className="text-sm text-gray-500 py-2">

                            {initialLoadingUsers ? 'Loading users...' : 'No users available'}

                          </div>

                        )}

                      </div>

                    )}



                    {assignmentForm.assigneeType === 'department' && (

                      <div className="space-y-2">

                        {departments && departments.length > 0 ? (

                          departments.map(dept => (

                            <label key={dept.id} className="flex items-center space-x-2 cursor-pointer hover:bg-blue-50/30 p-1 rounded">

                              <input

                                type="checkbox"

                                checked={assignmentForm.assigneeIds.includes(dept.id)}

                                onChange={(e) => {

                                  if (e.target.checked) {

                                    setAssignmentForm(prev => ({

                                      ...prev,

                                      assigneeIds: [...prev.assigneeIds, dept.id]

                                    }));

                                  } else {

                                    setAssignmentForm(prev => ({

                                      ...prev,

                                      assigneeIds: prev.assigneeIds.filter(id => id !== dept.id)

                                    }));

                                  }

                                }}

                                className="h-4 w-4 text-brand focus:ring-0 border-gray-300 rounded"

                              />

                              <span className="text-sm capitalize">{dept.name}{dept.count > 0 ? ` (${dept.count} people)` : ''}</span>

                            </label>

                          ))

                        ) : (

                          <div className="text-sm text-gray-500 py-2">No departments available</div>

                        )}

                      </div>

                    )}



                  </div>

                  {touchedFields.has('assigneeIds') && formErrors.assigneeIds && (

                    <p className="mt-1 text-sm text-red-600 flex items-center">

                      <AlertCircle className="h-4 w-4 mr-1" />

                      {formErrors.assigneeIds}

                    </p>

                  )}

                </div>



                {/* Due Date */}

                <div>

                  <label className="block text-sm font-medium text-gray-700 mb-2">Due Date *</label>

                  <input

                    type="date"

                    name="dueDate"

                    data-field="dueDate"

                    value={assignmentForm.dueDate || ''}

                    min={new Date().toISOString().split('T')[0]}

                    onChange={(e) => {

                      const selectedDate = e.target.value;

                      const today = new Date().toISOString().split('T')[0];



                      setAssignmentForm(prev => ({ ...prev, dueDate: selectedDate }));



                      // Validate past date

                      let error = null;

                      if (selectedDate && selectedDate < today) {

                        error = 'Due date cannot be in the past';

                      }



                      // Only set error if field is touched

                      if (touchedFields.has('dueDate')) {

                        setFormErrors(prev => ({ ...prev, dueDate: error }));

                      } else {

                        setFormErrors(prev => ({ ...prev, dueDate: null }));

                      }

                    }}

                    onBlur={() => handleFieldBlur('dueDate')}

                    className={`input-field px-3 py-2 ${touchedFields.has('dueDate') && formErrors.dueDate ? 'border-red-500 bg-red-50' : ''

                      }`}

                  />

                  {touchedFields.has('dueDate') && formErrors.dueDate && (

                    <p className="mt-1 text-sm text-red-600 flex items-center">

                      <AlertCircle className="h-4 w-4 mr-1" />

                      {formErrors.dueDate}

                    </p>

                  )}

                </div>



                {/* Email Notification */}

                <div>

                  <h3 className="text-lg font-medium text-gray-900 mb-3">Email Notification</h3>

                  <div className="space-y-3">

                    <div className="flex items-center space-x-3">

                      <input

                        type="checkbox"

                        checked={assignmentForm.sendEmail}

                        onChange={(e) => setAssignmentForm(prev => ({

                          ...prev,

                          sendEmail: e.target.checked

                        }))}

                        className="h-4 w-4 text-brand focus:ring-0 border-gray-300 rounded"

                      />

                      <Mail className="h-4 w-4 text-gray-500" />

                      <span className="text-sm">Send assignment notification via email</span>

                    </div>

                    <p className="text-xs text-gray-500 ml-7">Selected assignees will receive a professional email notification about their new assignment</p>

                  </div>

                </div>



                {/* Notes */}

                <div>

                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>

                  <textarea

                    name="notes"

                    data-field="notes"

                    value={assignmentForm.notes}

                    maxLength={3000}

                    onKeyPress={(e) => {

                      const char = e.key;

                      if (!/^[a-zA-Z0-9\s\-_&().,;:!?'"]$/.test(char) && char !== 'Backspace' && char !== 'Delete' && char !== 'ArrowLeft' && char !== 'ArrowRight' && char !== 'Tab' && char !== 'Enter') {

                        e.preventDefault();

                      }

                    }}

                    onChange={(e) => {

                      let value = e.target.value;

                      value = value.replace(/[^a-zA-Z0-9\s\-_&().,;:!?'"]/g, '');

                      if (value.length > 1000) {

                        value = value.substring(0, 3000);

                      }

                      setAssignmentForm(prev => ({ ...prev, notes: value }));



                      // Real-time validation for notes

                      let error = null;

                      if (value && value.trim().length > 0) {

                        if (value.trim().length > 3000) {

                          error = 'Notes cannot exceed 3000 characters';

                        } else if (!/^[a-zA-Z0-9\s\-_&().,;:!?'"]*$/.test(value.trim())) {

                          error = 'Notes contains invalid characters. Only letters, numbers, spaces, and basic punctuation are allowed';

                        }

                      }



                      // Only set error if field is touched

                      if (touchedFields.has('notes')) {

                        setFormErrors(prev => ({ ...prev, notes: error }));

                      } else {

                        setFormErrors(prev => ({ ...prev, notes: null }));

                      }

                    }}

                    onBlur={() => handleFieldBlur('notes')}

                    rows={3}

                    className={`input-field px-3 py-2 resize-none ${touchedFields.has('notes') && formErrors.notes ? 'border-red-500 bg-red-50' : ''

                      }`}

                    placeholder="Add any additional notes..."

                  />

                  <div className="flex items-center justify-between mt-1">

                    {touchedFields.has('notes') && formErrors.notes && (

                      <p className="text-sm text-red-600 flex items-center">

                        <AlertCircle className="h-4 w-4 mr-1" />

                        {formErrors.notes}

                      </p>

                    )}

                    <span className={`text-xs ml-auto ${assignmentForm.notes.length > 900 ? 'text-red-600' :

                      assignmentForm.notes.length > 800 ? 'text-yellow-600' :

                        'text-gray-500'

                      }`}>

                      {assignmentForm.notes.length}/3000

                    </span>

                  </div>

                </div>

              </div>

            </div>



            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end space-x-3">

              <PermissionGate permission="training" action="read">

                <button

                  onClick={() => {

                    setShowCreateAssignment(false);

                    setFormErrors({});

                    setTouchedFields(new Set());

                    setEditingAssignment(null);

                  }}

                  className="btn-cancel"

                >

                  Cancel

                </button>

              </PermissionGate>

              <PermissionGate permission="training" action={editingAssignment ? "update" : "create"}>

                <button

                  onClick={handleSaveAssignment}

                  disabled={isSubmitting}

                  className="btn-create disabled:opacity-50 disabled:cursor-not-allowed"

                >

                  {isSubmitting ? (

                    <>

                      <RefreshCw className="h-4 w-4 mr-2 inline animate-spin" />

                      {editingAssignment ? 'Updating...' : 'Creating...'}

                    </>

                  ) : (

                    <>

                      <Save className="h-4 w-4 mr-2 inline" />

                      {editingAssignment ? 'Update Assignment' : 'Create Assignment'}

                    </>

                  )}

                </button>

              </PermissionGate>

            </div>

          </div>

        </div>

      )}



      {/* Delete Confirmation Modal */}

      {showDeleteConfirm && (

        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">

          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">

            <div className="flex items-center mb-4">

              <div className="flex-shrink-0 w-10 h-10 mx-auto bg-brand-light bg-opacity-10 rounded-full flex items-center justify-center">

                <AlertTriangle className="h-6 w-6 text-red-600" />

              </div>

            </div>



            <div className="text-center">

              <h3 className="text-lg font-medium text-gray-900 mb-2">

                Delete Assignment

              </h3>

              <p className="text-sm text-gray-500 mb-6">

                Are you sure you want to delete this assignment? This action cannot be undone and will remove the assignment for all assignees.

              </p>



              <div className="flex space-x-3 justify-center">

                <button

                  onClick={cancelDeleteAssignment}

                  className="btn-cancel"

                  disabled={deleteAssignmentMutation.isPending}

                >

                  Cancel

                </button>

                <button

                  onClick={confirmDeleteAssignment}

                  className="btn-primary disabled:opacity-50"

                  disabled={deleteAssignmentMutation.isPending}

                >

                  {deleteAssignmentMutation.isPending ? 'Deleting...' : 'Delete Assignment'}

                </button>

              </div>

            </div>

          </div>

        </div>

      )}

      {/* Bulk Delete Confirmation Modal */}

      {showBulkDeleteModal && (

        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">

          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">

            <div className="flex items-center mb-4">

              <AlertTriangle className="h-6 w-6 text-red-600 mr-3" />

              <h3 className="text-lg font-semibold text-gray-900">Confirm Bulk Deletion</h3>

            </div>

            <p className="text-gray-600 mb-2">

              Are you sure you want to delete{' '}

              <span className="font-semibold text-red-600">{selectedRows.size}</span>{' '}

              selected assignment{selectedRows.size !== 1 ? 's' : ''}?

            </p>

            <p className="text-sm text-gray-500 mb-6">This action cannot be undone and will remove the assignment for all assignees.</p>

            <div className="flex items-center justify-end space-x-3">

              <button

                onClick={() => setShowBulkDeleteModal(false)}

                disabled={isBulkDeleting}

                className="btn-cancel disabled:opacity-50"

              >

                Cancel

              </button>

              <button

                onClick={confirmBulkDelete}

                disabled={isBulkDeleting}

                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center"

              >

                {isBulkDeleting ? (

                  <>

                    <span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />

                    Deleting...

                  </>

                ) : (

                  <>

                    <Trash2 className="h-4 w-4 mr-2" />

                    Delete {selectedRows.size} Assignment{selectedRows.size !== 1 ? 's' : ''}

                  </>

                )}

              </button>

            </div>

          </div>

        </div>

      )}

      {/* Export Modal */}

      <ExportModal

        isOpen={showExportModal}

        onClose={() => setShowExportModal(false)}

        onExportCurrentPage={handleExportCurrentPage}

        onExportRange={handleExportRange}

        onExportAll={handleExportAll}

        loading={exportLoading}

        title="Export Assignments"

      />

    </div >

  );

};



export default AssignmentsEnrollment;

